import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/ui/table';
import { Skeleton } from '@/ui/skeleton';
import { CodeReviewSummary } from '@/shared/services/supabaseService';
import { formatDistanceToNow } from 'date-fns';
import { ScrollArea } from '@/ui/scroll-area';
import { AlertTriangle, CheckCircle, Clock, XCircle } from 'lucide-react';
import { Badge } from '@/ui/badge';

interface RecentScansProps {
  data: CodeReviewSummary[];
  loading?: boolean;
}

const RecentScans: React.FC<RecentScansProps> = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="h-full space-y-4">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="flex items-center space-x-4">
            <Skeleton className="h-12 w-full" />
          </div>
        ))}
      </div>
    );
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'in_progress':
        return <Clock className="w-4 h-4 text-blue-500 animate-pulse" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-neutral-400" />;
    }
  };

  return (
    <div className="h-full">
        <ScrollArea className="h-[400px] border border-neutral-950 dark:border-neutral-100 bg-background">
        <Table>
            <TableHeader className="bg-neutral-100 dark:bg-neutral-900 sticky top-0 z-10">
            <TableRow className="hover:bg-transparent border-b border-neutral-950 dark:border-neutral-100">
                <TableHead className="font-mono text-xs uppercase text-neutral-950 dark:text-neutral-100 font-bold h-10 w-[50px]">Status</TableHead>
                <TableHead className="font-mono text-xs uppercase text-neutral-950 dark:text-neutral-100 font-bold h-10">Context</TableHead>
                <TableHead className="font-mono text-xs uppercase text-neutral-950 dark:text-neutral-100 font-bold h-10">Issues</TableHead>
                <TableHead className="font-mono text-xs uppercase text-neutral-950 dark:text-neutral-100 font-bold h-10 text-right">Date</TableHead>
            </TableRow>
            </TableHeader>
            <TableBody>
            {data && data.length > 0 ? (
                data.map((scan) => (
                <TableRow key={scan.id} className="border-b border-neutral-950 dark:border-neutral-100 hover:bg-neutral-50 dark:hover:bg-neutral-900/20 transition-colors">
                    <TableCell className="py-3">
                        {getStatusIcon(scan.status)}
                    </TableCell>
                    <TableCell className="py-3">
                    <div className="font-bold font-mono text-sm mb-1 truncate max-w-[300px]" title={scan.title}>
                        {scan.title}
                    </div>
                    <div className="text-xs font-mono text-muted-foreground">
                        {scan.repositoryName} #{scan.pullRequestNumber}
                    </div>
                    </TableCell>
                    <TableCell className="py-3">
                        {scan.issuesFound > 0 ? (
                             <Badge variant="destructive" className="font-mono text-xs">
                                <AlertTriangle className="w-3 h-3 mr-1" />
                                {scan.issuesFound} Found
                             </Badge>
                        ) : (
                            <Badge variant="outline" className="font-mono text-xs bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Clean
                            </Badge>
                        )}
                    </TableCell>
                    <TableCell className="text-right text-xs font-mono text-muted-foreground py-3">
                    {scan.createdAt ? formatDistanceToNow(new Date(scan.createdAt), { addSuffix: true }) : 'N/A'}
                    </TableCell>
                </TableRow>
                ))
            ) : (
                <TableRow>
                <TableCell colSpan={4} className="h-24 text-center text-muted-foreground font-mono">
                    No recent scans found. Connect a repository to get started.
                </TableCell>
                </TableRow>
            )}
            </TableBody>
        </Table>
        </ScrollArea>
    </div>
  );
};

export default RecentScans;
